import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Center, Loader } from '@mantine/core';
import { QUERY_KEYS } from '@/constants';
import {
  useAuth,
  useChurchAdminCurrentScopes,
  useImpersonation,
  useLastSelectedChurch,
  useTokenExchangeMutation,
} from '@/hooks';
import { AUTH_STATE, LsStoreKey } from '@/types';
import AuthService from '@/services/auth.service';
import { impersonationService } from '@/services/impersonation.service';
import { ROUTE_ALIASES } from '@/routing/routesAliases';
import {
  getSuperadminHandoffParamsFromLocation,
  handleError,
  parseImpersonationStateFromRawStateParam,
  setDataToLs,
} from '@/helpers';
import { SignInLanding } from './SignInLanding';

type SignInRedirectParams = {
  impersonatedUserEmail: string;
  impersonatedUserName: string;
  returnToControlCenterUrl: string;
};

const getSignInRedirectState = ({
  impersonatedUserEmail,
  impersonatedUserName,
  returnToControlCenterUrl,
}: SignInRedirectParams): Record<string, string> | undefined => {
  const redirectState: Record<string, string> = {};

  if (impersonatedUserEmail) {
    redirectState['X-Impersonate-User'] = impersonatedUserEmail;
  }

  if (impersonatedUserName) {
    redirectState.impersonatedUserName = impersonatedUserName;
  }

  if (returnToControlCenterUrl) {
    redirectState.returnToControlCenterUrl = returnToControlCenterUrl;
  }

  if (Object.keys(redirectState).length === 0) {
    return undefined;
  }

  return redirectState;
};

export const Login = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state, bumpAuthEvaluation } = useAuth();
  const location = useLocation();
  const origin = location.state?.from?.pathname || ROUTE_ALIASES.HOME;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const churchIdFromQuery = searchParams.get('church_id')?.trim() || '';
  const impersonatedUserEmail = searchParams.get('X-Impersonate-User')?.trim() || '';
  const impersonatedUserName = searchParams.get('impersonatedUserName')?.trim() || '';
  const returnToControlCenterUrl = searchParams.get('returnToControlCenterUrl')?.trim() || '';
  const ottParam = searchParams.get('ott')?.trim() || '';
  const inviteIdParam = searchParams.get('inviteId')?.trim() || '';
  const publicTokenParam = searchParams.get('token')?.trim() || '';
  const invitedUserNameParam = searchParams.get('userName')?.trim() || searchParams.get('username')?.trim() || '';
  const isInviteOttFlow = Boolean(ottParam && inviteIdParam && invitedUserNameParam);
  const handoffParams = useMemo(
    () => getSuperadminHandoffParamsFromLocation({ search: location.search, hash: location.hash }),
    [location.hash, location.search]
  );
  const isSuperadminHandoffFlow = Boolean(handoffParams.accessToken && handoffParams.rawState) && !isInviteOttFlow;
  const [inviteOttExchangeSettled, setInviteOttExchangeSettled] = useState(!isInviteOttFlow);
  const [isInviteOttExchangeFailed, setIsInviteOttExchangeFailed] = useState(false);
  const inviteOttExchangeRequestedRef = useRef(false);
  const [superadminHandoffSettled, setSuperadminHandoffSettled] = useState(!isSuperadminHandoffFlow);
  const [isSuperadminHandoffFailed, setIsSuperadminHandoffFailed] = useState(false);
  const superadminHandoffRequestedRef = useRef(false);
  const { mutate: exchangeInvitationToken } = useTokenExchangeMutation();
  const hasImpersonationBootstrapData = Boolean(
    impersonatedUserEmail ||
    impersonatedUserName ||
    returnToControlCenterUrl ||
    (handoffParams.accessToken && handoffParams.rawState)
  );
  const hasLogoutIntent = AuthService.hasLogoutIntent();
  const isLoggedOut =
    !isInviteOttFlow && (searchParams.get('logged_out') === '1' || (hasLogoutIntent && !hasImpersonationBootstrapData));
  const { isImpersonating } = useImpersonation();
  const { data: currentScope, isPlaceholderData: isLoginScopesPlaceholder } = useChurchAdminCurrentScopes({
    enabled: state === AUTH_STATE.AUTHENTICATED && Boolean(churchIdFromQuery),
    filters: isImpersonating ? { limit: 500, page: 1 } : undefined,
  });
  const { initializeLastSelectedChurch } = useLastSelectedChurch();
  const isRedirecting = useRef(false);
  const hasProgrammaticSsoParams = Boolean(impersonatedUserEmail || impersonatedUserName || returnToControlCenterUrl);
  const shouldAutoStartSso = hasProgrammaticSsoParams || isInviteOttFlow || isSuperadminHandoffFlow;

  useEffect(() => {
    if (!isInviteOttFlow) {
      return;
    }

    if (isSuperadminHandoffFlow) {
      return;
    }

    if (state === AUTH_STATE.LOADING) {
      return;
    }

    if (state !== AUTH_STATE.UNAUTHENTICATED) {
      return;
    }

    if (inviteOttExchangeRequestedRef.current) {
      return;
    }

    inviteOttExchangeRequestedRef.current = true;
    exchangeInvitationToken(
      {
        inviteId: inviteIdParam,
        ott: ottParam,
        userName: invitedUserNameParam,
        publicToken: publicTokenParam,
      },
      {
        onSuccess: (data) => {
          AuthService.clearLogoutIntent();
          AuthService.applyInvitationAccessToken(data.accessToken);
          void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMIN_CURRENT_SCOPES] });
          if (churchIdFromQuery) {
            setDataToLs(LsStoreKey.LoginChurchId, churchIdFromQuery);
          }
          bumpAuthEvaluation();
        },
        onSettled: () => {
          setInviteOttExchangeSettled(true);
        },
        onError: (error) => {
          setIsInviteOttExchangeFailed(true);
          handleError(error);
        },
      }
    );
  }, [
    bumpAuthEvaluation,
    churchIdFromQuery,
    exchangeInvitationToken,
    queryClient,
    inviteIdParam,
    invitedUserNameParam,
    isInviteOttFlow,
    ottParam,
    publicTokenParam,
    state,
    isSuperadminHandoffFlow,
  ]);

  useEffect(() => {
    if (!isSuperadminHandoffFlow) {
      return;
    }

    if (isInviteOttFlow) {
      return;
    }

    if (state === AUTH_STATE.LOADING) {
      return;
    }

    if (state !== AUTH_STATE.UNAUTHENTICATED) {
      return;
    }

    if (superadminHandoffRequestedRef.current) {
      return;
    }

    if (!handoffParams.accessToken || !handoffParams.rawState) {
      return;
    }

    const accessToken = handoffParams.accessToken;
    const { idToken, refreshToken, expiresIn, tokenType, scope, rawState } = handoffParams;

    superadminHandoffRequestedRef.current = true;

    const impersonationState = parseImpersonationStateFromRawStateParam(rawState);
    if (!impersonationState) {
      setIsSuperadminHandoffFailed(true);
      setSuperadminHandoffSettled(true);
      return;
    }

    const handleImpersonationHandoff = async (): Promise<void> => {
      AuthService.clearLogoutIntent();

      if (!AuthService.hasOidcUserInSessionStorage()) {
        await AuthService.applyImpersonationHandoff({
          accessToken,
          idToken,
          refreshToken,
          expiresIn,
          tokenType,
          scope,
        });
      }

      impersonationService.applyFromOidcState({ state: impersonationState });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMIN_CURRENT_SCOPES] });
      bumpAuthEvaluation();

      const churchIdFromQuery = searchParams.get('church_id')?.trim();
      const churchIdFromHandoff = impersonationState.church_id?.trim();
      const churchId = churchIdFromQuery || churchIdFromHandoff;
      const nextSearch = churchId ? `?church_id=${encodeURIComponent(churchId)}` : '';
      navigate(`${location.pathname}${nextSearch}`, { replace: true });

      if (churchId) {
        setDataToLs(LsStoreKey.LoginChurchId, churchId);
      }

      setSuperadminHandoffSettled(true);
    };

    void handleImpersonationHandoff();
  }, [
    bumpAuthEvaluation,
    handoffParams.accessToken,
    handoffParams.rawState,
    isInviteOttFlow,
    isSuperadminHandoffFlow,
    location.pathname,
    navigate,
    queryClient,
    searchParams,
    state,
  ]);

  useEffect(() => {
    if (!churchIdFromQuery) {
      return;
    }

    if (isInviteOttFlow && !inviteOttExchangeSettled) {
      return;
    }

    if (isSuperadminHandoffFlow && !superadminHandoffSettled) {
      return;
    }

    setDataToLs(LsStoreKey.LoginChurchId, churchIdFromQuery);
  }, [churchIdFromQuery, inviteOttExchangeSettled, isInviteOttFlow, isSuperadminHandoffFlow, superadminHandoffSettled]);

  useEffect(() => {
    if (state !== AUTH_STATE.UNAUTHENTICATED) {
      return;
    }

    if (isLoggedOut) {
      return;
    }

    // Keep the admin origin stable so users bookmark this page instead of a one-time Cognito URL.
    if (!shouldAutoStartSso) {
      return;
    }

    if (isRedirecting.current) {
      return;
    }

    if (isInviteOttFlow && !inviteOttExchangeSettled) {
      return;
    }

    if (isSuperadminHandoffFlow && !superadminHandoffSettled) {
      return;
    }

    if (isInviteOttExchangeFailed) {
      return;
    }

    if (isSuperadminHandoffFailed) {
      return;
    }

    isRedirecting.current = true;
    AuthService.signinRedirect({
      state: getSignInRedirectState({
        impersonatedUserEmail,
        impersonatedUserName,
        returnToControlCenterUrl,
      }),
    });
  }, [
    impersonatedUserEmail,
    impersonatedUserName,
    inviteOttExchangeSettled,
    isInviteOttFlow,
    isInviteOttExchangeFailed,
    isLoggedOut,
    isSuperadminHandoffFailed,
    isSuperadminHandoffFlow,
    superadminHandoffSettled,
    returnToControlCenterUrl,
    shouldAutoStartSso,
    state,
  ]);

  useEffect(() => {
    if (state !== AUTH_STATE.AUTHENTICATED) {
      return;
    }

    if (!churchIdFromQuery) {
      return;
    }

    if (!currentScope?.scopes?.length) {
      return;
    }

    if (isLoginScopesPlaceholder) {
      return;
    }

    initializeLastSelectedChurch(currentScope.scopes);
  }, [churchIdFromQuery, currentScope, initializeLastSelectedChurch, isLoginScopesPlaceholder, state]);

  const handleSignIn = (): void => {
    AuthService.clearLogoutIntent();
    void AuthService.signinRedirect({
      state: getSignInRedirectState({
        impersonatedUserEmail,
        impersonatedUserName,
        returnToControlCenterUrl,
      }),
    });
  };

  const handleDismissSuperadminHandoffFailure = (): void => {
    window.location.assign(ROUTE_ALIASES.LOGIN);
  };

  if (isLoggedOut) {
    return <SignInLanding title="You are signed out" signInLabel="Sign in again" onSignIn={handleSignIn} />;
  }

  if (state === AUTH_STATE.AUTHENTICATED) {
    return <Navigate to={origin} />;
  }

  if (isInviteOttExchangeFailed) {
    return <SignInLanding title="Unable to verify the invitation link" signInLabel="Sign in" onSignIn={handleSignIn} />;
  }

  if (isSuperadminHandoffFailed) {
    return (
      <SignInLanding
        title="Unable to start impersonation"
        description="The sign-in link is invalid or expired. Return to the control center and try again."
        signInLabel="Back to sign in"
        onSignIn={handleDismissSuperadminHandoffFailure}
      />
    );
  }

  const isWaitingForBootstrap =
    state === AUTH_STATE.LOADING ||
    (isInviteOttFlow && !inviteOttExchangeSettled) ||
    (isSuperadminHandoffFlow && !superadminHandoffSettled) ||
    shouldAutoStartSso;

  if (isWaitingForBootstrap) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <SignInLanding signInLabel="Sign in" onSignIn={handleSignIn} />;
};
